import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ReactFlow, Background, Controls, MiniMap, addEdge, applyEdgeChanges, applyNodeChanges,
  ReactFlowProvider, useReactFlow, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { motion } from "framer-motion";
import {
  Play, Stop, CheckSquare, GitBranch, Lightning, BellSimple, Clock, PenNib, Sparkle,
  ListChecks, FloppyDisk, Rocket, ArrowsCounterClockwise, Trash, X, User as UserIcon,
  CodeSimple, ArrowLeft, DotsSixVertical, GitFork, ArrowsOutSimple, ArrowsInSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";

const ICON_MAP = {
  start: Play, end: Stop, approval: CheckSquare, decision: GitBranch,
  action: Lightning, notification: BellSimple, wait: Clock, sign: PenNib,
  ai_analyze: Sparkle, form: ListChecks, fork: ArrowsOutSimple, join: ArrowsInSimple,
};

/* ------------- CUSTOM NODE RENDERER ------------- */
function NodeCard({ data, selected }) {
  const Icon = ICON_MAP[data.type] || Lightning;
  return (
    <div
      className={`relative px-4 py-3 rounded-lg border transition min-w-[180px] ${
        selected ? "border-[var(--accent)] shadow-lg" : "border-[var(--border-strong)]"
      } glass-strong`}
      style={{ boxShadow: selected ? `0 0 0 2px ${data.color}40` : undefined }}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: `${data.color}25`, border: `1px solid ${data.color}` }}>
          <Icon size={12} weight="fill" style={{ color: data.color }} />
        </div>
        <span className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: data.color }}>{data.type.replace("_", " ")}</span>
      </div>
      <div className="text-sm font-semibold leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>{data.label}</div>
      {data.executor_type && data.executor_type !== "auto" && (
        <div className="mt-1.5 text-[10px] text-[var(--text-3)] flex items-center gap-1">
          <UserIcon size={8} /> {data.executor_type}: {data.executor_ref || "—"}
        </div>
      )}
      {data.executor_type === "auto" && (
        <div className="mt-1.5 text-[10px] text-[var(--text-3)] font-mono">auto</div>
      )}
    </div>
  );
}

const nodeTypes = { custom: NodeCard };

function toFlow(wfNodes = [], wfEdges = []) {
  const nodes = wfNodes.map((n) => ({
    id: n.id,
    type: "custom",
    position: n.position || { x: 100, y: 100 },
    data: {
      label: n.label,
      type: n.type,
      color: n.color || "#38BDF8",
      executor_type: n.executor_type,
      executor_ref: n.executor_ref,
      config: n.config || {},
    },
  }));
  const edges = wfEdges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label,
    data: { condition: e.condition || { type: "always", expression: "", label: e.label || "" } },
    labelBgPadding: [6, 3],
    labelBgBorderRadius: 6,
    labelBgStyle: { fill: "var(--bg-2)", stroke: "var(--border-strong)", strokeWidth: 1 },
    labelStyle: { fill: "var(--text)", fontWeight: 600, fontSize: 10 },
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed, color: "#38BDF8" },
    style: { stroke: "#38BDF8", strokeWidth: 2 },
  }));
  return { nodes, edges };
}

function fromFlow(nodes, edges) {
  const wfNodes = nodes.map((n) => ({
    id: n.id,
    type: n.data.type,
    label: n.data.label,
    position: n.position,
    color: n.data.color,
    executor_type: n.data.executor_type,
    executor_ref: n.data.executor_ref,
    config: n.data.config || {},
  }));
  const wfEdges = edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label,
    condition: e.data?.condition || { type: "always", expression: "", label: e.label || "" },
  }));
  return { nodes: wfNodes, edges: wfEdges };
}

/* ------------- MAIN DESIGNER ------------- */
function Designer() {
  const { id } = useParams();
  const nav = useNavigate();
  const [wf, setWf] = useState(null);
  const [flowNodes, setFlowNodes] = useState([]);
  const [flowEdges, setFlowEdges] = useState([]);
  const [nodeTypesMeta, setNodeTypesMeta] = useState([]);
  const [triggerEvents, setTriggerEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [saving, setSaving] = useState(false);
  const rfRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get(`/workflows/${id}`),
      api.get("/workflows/meta/node-types"),
    ]).then(([w, m]) => {
      setWf(w.data);
      const { nodes, edges } = toFlow(w.data.nodes, w.data.edges);
      setFlowNodes(nodes);
      setFlowEdges(edges);
      setNodeTypesMeta(m.data.types);
      setTriggerEvents(m.data.trigger_events || []);
    });
  }, [id]);

  const readOnly = wf?.status && wf.status !== "draft";

  const onNodesChange = useCallback((changes) => setFlowNodes((nds) => applyNodeChanges(changes, nds)), []);
  const onEdgesChange = useCallback((changes) => setFlowEdges((eds) => applyEdgeChanges(changes, eds)), []);
  const onConnect = useCallback(
    (params) => setFlowEdges((eds) => addEdge({
      ...params,
      id: `e-${Date.now()}`,
      animated: true,
      markerEnd: { type: MarkerType.ArrowClosed, color: "#38BDF8" },
      style: { stroke: "#38BDF8", strokeWidth: 2 },
      data: { condition: { type: "always", expression: "", label: "" } },
    }, eds)),
    []
  );
  const onNodeClick = (_e, n) => { setSelected(n); setSelectedEdge(null); };
  const onEdgeClick = (_e, e) => { setSelectedEdge(e); setSelected(null); };
  const onPaneClick = () => { setSelected(null); setSelectedEdge(null); };

  const updateEdgeLabel = (label) => {
    if (!selectedEdge) return;
    setFlowEdges((eds) => eds.map((e) => e.id === selectedEdge.id ? {
      ...e,
      label,
      data: { ...(e.data || {}), condition: { ...(e.data?.condition || { type: "always", expression: "" }), label } },
    } : e));
    setSelectedEdge({
      ...selectedEdge,
      label,
      data: { ...(selectedEdge.data || {}), condition: { ...(selectedEdge.data?.condition || { type: "always", expression: "" }), label } },
    });
  };

  const updateEdgeCondition = (patch) => {
    if (!selectedEdge) return;
    const cur = selectedEdge.data?.condition || { type: "always", expression: "", label: selectedEdge.label || "" };
    const next = { ...cur, ...patch };
    // When the label inside the condition is edited, reflect it on the visible edge label too
    const visibleLabel = next.type === "branch" || next.type === "expression" ? (next.label || selectedEdge.label || "") : selectedEdge.label;
    setFlowEdges((eds) => eds.map((e) => e.id === selectedEdge.id ? {
      ...e,
      label: visibleLabel,
      data: { ...(e.data || {}), condition: next },
    } : e));
    setSelectedEdge({
      ...selectedEdge,
      label: visibleLabel,
      data: { ...(selectedEdge.data || {}), condition: next },
    });
  };

  const deleteEdge = () => {
    if (!selectedEdge) return;
    setFlowEdges((eds) => eds.filter((e) => e.id !== selectedEdge.id));
    setSelectedEdge(null);
  };

  const addNode = (type) => {
    if (readOnly) return toast.error("Only draft workflows can be edited");
    const meta = nodeTypesMeta.find((t) => t.id === type);
    const newId = `${type}-${Date.now()}`;
    const position = { x: 300 + Math.random() * 200, y: 150 + Math.random() * 200 };
    setFlowNodes((n) => [...n, {
      id: newId,
      type: "custom",
      position,
      data: { label: meta.label, type, color: meta.color, executor_type: type === "approval" || type === "sign" ? "role" : "auto", executor_ref: type === "approval" ? "manager" : null, config: {} },
    }]);
  };

  const deleteSelected = () => {
    if (!selected) return;
    if (selected.data.type === "start" || selected.data.type === "end") return toast.error("Cannot delete Start/End nodes");
    setFlowNodes((n) => n.filter((x) => x.id !== selected.id));
    setFlowEdges((e) => e.filter((x) => x.source !== selected.id && x.target !== selected.id));
    setSelected(null);
  };

  const updateSelected = (patch) => {
    if (!selected) return;
    setFlowNodes((n) => n.map((x) => x.id === selected.id ? { ...x, data: { ...x.data, ...patch } } : x));
    setSelected({ ...selected, data: { ...selected.data, ...patch } });
  };

  const save = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      const { nodes, edges } = fromFlow(flowNodes, flowEdges);
      const { data } = await api.patch(`/workflows/${id}`, { name: wf.name, description: wf.description, nodes, edges });
      setWf(data);
      toast.success("Draft saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    await save();
    try {
      const { data } = await api.post(`/workflows/${id}/publish`);
      setWf(data);
      toast.success(`Published as v${data.version}`);
      nav("/app/workflows");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };

  const newVersion = async () => {
    try {
      const { data } = await api.post(`/workflows/${id}/new-version`);
      toast.success("New draft created");
      nav(`/app/workflows/designer/${data.id}`);
    } catch (e) { toast.error("Failed"); }
  };

  if (!wf) return <PageShell title="Workflow Designer"><div className="h-64 glass animate-pulse" /></PageShell>;

  return (
    <PageShell
      title={wf.name}
      subtitle={`Version ${wf.version} · ${wf.status}${readOnly ? " (read-only)" : " (editing)"}`}
      right={
        <>
          <Button variant="ghost" size="sm" onClick={() => nav("/app/workflows")} data-testid="designer-back-btn" className="gap-2">
            <ArrowLeft size={14} /> Back
          </Button>
          {readOnly ? (
            <Button onClick={newVersion} data-testid="designer-new-version-btn" className="gap-2">
              <GitFork size={14} /> New version
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={save} disabled={saving} data-testid="designer-save-btn" className="gap-2 glass-subtle border-[var(--border-strong)]">
                <FloppyDisk size={14} /> {saving ? "Saving…" : "Save draft"}
              </Button>
              <Button onClick={publish} data-testid="designer-publish-btn" className="gap-2">
                <Rocket size={14} /> Publish
              </Button>
            </>
          )}
        </>
      }
    >
      <div className="grid grid-cols-12 gap-4 h-[calc(100vh-140px)]">
        {/* Palette */}
        <GlassCard className="col-span-2 p-3 overflow-y-auto">
          <div className="overline mb-3">Activities</div>
          <div className="space-y-1.5">
            {nodeTypesMeta.map((t) => {
              const Icon = ICON_MAP[t.id] || Lightning;
              return (
                <button
                  key={t.id}
                  onClick={() => addNode(t.id)}
                  disabled={readOnly || t.id === "start" || t.id === "end"}
                  data-testid={`palette-${t.id}`}
                  className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-[var(--surface-2)] border border-transparent hover:border-[var(--border-strong)] transition disabled:opacity-40 disabled:cursor-not-allowed"
                  title={t.description}
                >
                  <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: `${t.color}25`, border: `1px solid ${t.color}` }}>
                    <Icon size={12} weight="fill" style={{ color: t.color }} />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <div className="text-xs font-medium truncate">{t.label}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </GlassCard>

        {/* Canvas */}
        <div className="col-span-7 glass relative overflow-hidden rounded-xl">
          <ReactFlow
            ref={rfRef}
            nodes={flowNodes}
            edges={flowEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            edgesFocusable={!readOnly}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
            defaultEdgeOptions={{ animated: true, style: { stroke: "#38BDF8", strokeWidth: 2 } }}
          >
            <Background color="var(--border)" gap={24} />
            <Controls showInteractive={false} className="!bg-[var(--surface-strong)] !border-[var(--border-strong)] !rounded-md" />
            <MiniMap nodeColor={(n) => n.data?.color || "#38BDF8"} maskColor="rgba(0,0,0,0.4)" className="!bg-[var(--bg-2)] !border !border-[var(--border)]" pannable zoomable />
          </ReactFlow>
        </div>

        {/* Properties */}
        <GlassCard className="col-span-3 p-4 overflow-y-auto">
          {selectedEdge ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="overline">Connection</div>
                {!readOnly && <button onClick={deleteEdge} data-testid="designer-delete-edge-btn" className="text-[var(--danger)] hover:bg-[var(--surface-2)] p-1 rounded"><Trash size={14} /></button>}
              </div>
              <div className="space-y-3">
                <div className="text-xs text-[var(--text-3)] font-mono truncate">
                  {selectedEdge.source} → {selectedEdge.target}
                </div>

                <div>
                  <Label className="text-xs text-[var(--text-2)]">When should this path be taken?</Label>
                  <Select
                    value={selectedEdge.data?.condition?.type || "always"}
                    onValueChange={(v) => updateEdgeCondition({ type: v })}
                    disabled={readOnly}
                  >
                    <SelectTrigger data-testid="designer-edge-condition-type" className="mt-1 glass-subtle border-[var(--border)] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="always">Always (default path)</SelectItem>
                      <SelectItem value="branch">On branch label (decision node)</SelectItem>
                      <SelectItem value="expression">When expression is true</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-[var(--text-2)]">Display label</Label>
                  <Input
                    value={selectedEdge.label || ""}
                    disabled={readOnly}
                    onChange={(e) => updateEdgeLabel(e.target.value)}
                    placeholder="e.g. Approved / Yes / >$100k"
                    data-testid="designer-edge-label-input"
                    className="mt-1 glass-subtle border-[var(--border)] font-mono text-xs"
                  />
                </div>

                {selectedEdge.data?.condition?.type === "expression" && (
                  <div>
                    <Label className="text-xs text-[var(--text-2)]">Expression</Label>
                    <Textarea
                      rows={3}
                      value={selectedEdge.data?.condition?.expression || ""}
                      disabled={readOnly}
                      onChange={(e) => updateEdgeCondition({ expression: e.target.value })}
                      placeholder="amount > 50000 and status == 'approved'"
                      data-testid="designer-edge-condition-expr"
                      className="mt-1 glass-subtle border-[var(--border)] font-mono text-xs"
                    />
                    <div className="text-[10px] text-[var(--text-3)] mt-1 leading-snug">
                      Operators: <code>==  !=  &lt;  &lt;=  &gt;  &gt;=  and  or  not  in</code>. Variables come from the instance's <code>variables</code> bag — set them when calling <code>/advance</code> with <code>{"{variables:{amount: 85000}}"}</code>.
                    </div>
                  </div>
                )}

                {selectedEdge.data?.condition?.type === "branch" && (
                  <div className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)] text-[11px] text-[var(--text-2)]">
                    Follows this path when <code>/advance</code> is called with <code>{"{branch: \"" + (selectedEdge.data?.condition?.label || selectedEdge.label || "...") + "\"}"}</code>.
                  </div>
                )}

                {selectedEdge.data?.condition?.type === "always" && (
                  <div className="p-2 rounded-md bg-[var(--surface)] border border-[var(--border)] text-[11px] text-[var(--text-2)]">
                    Unconditional path. If multiple outgoing edges are "always", the first one wins.
                  </div>
                )}
              </div>
            </>
          ) : !selected ? (
            <>
              <div className="overline mb-3">Workflow</div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-[var(--text-2)]">Name</Label>
                  <Input value={wf.name} disabled={readOnly} onChange={(e) => setWf({ ...wf, name: e.target.value })} data-testid="designer-name-input" className="mt-1 glass-subtle border-[var(--border)]" />
                </div>
                <div>
                  <Label className="text-xs text-[var(--text-2)]">Description</Label>
                  <Textarea rows={3} value={wf.description || ""} disabled={readOnly} onChange={(e) => setWf({ ...wf, description: e.target.value })} data-testid="designer-desc-input" className="mt-1 glass-subtle border-[var(--border)]" />
                </div>
                <div className="p-3 rounded-md bg-[var(--surface)] border border-[var(--border)] text-xs text-[var(--text-2)] space-y-1">
                  <div className="flex justify-between"><span>Status</span><span className="chip chip-neutral">{wf.status}</span></div>
                  <div className="flex justify-between"><span>Version</span><span className="font-mono">{wf.version}</span></div>
                  <div className="flex justify-between"><span>Nodes</span><span className="font-mono">{flowNodes.length}</span></div>
                  <div className="flex justify-between"><span>Edges</span><span className="font-mono">{flowEdges.length}</span></div>
                </div>

                {/* TRIGGERS */}
                <div className="pt-3 border-t border-[var(--border)]">
                  <div className="overline flex items-center gap-1 mb-2"><Lightning size={10} className="text-amber-400" /> Auto-triggers</div>
                  <div className="text-[10px] text-[var(--text-3)] mb-2">Auto-start instances when events fire. Published workflows only.</div>
                  <div className="space-y-1.5">
                    {triggerEvents.map((ev) => {
                      const enabled = (wf.triggers || []).some((t) => t.event === ev);
                      return (
                        <label key={ev} className="flex items-center gap-2 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            checked={enabled}
                            disabled={wf.status !== "published"}
                            data-testid={`trigger-${ev}`}
                            onChange={async (e) => {
                              const newTriggers = e.target.checked
                                ? [...(wf.triggers || []), { event: ev }]
                                : (wf.triggers || []).filter((t) => t.event !== ev);
                              try {
                                const { data } = await api.patch(`/workflows/${id}/triggers`, { triggers: newTriggers });
                                setWf(data);
                                toast.success("Triggers updated");
                              } catch { toast.error("Failed — publish workflow first"); }
                            }}
                            className="accent-[var(--accent)]"
                          />
                          <span className="font-mono">{ev}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-3 border-t border-[var(--border)] text-xs text-[var(--text-3)] leading-relaxed">
                  <div className="font-semibold mb-1 text-[var(--text-2)]">Tips</div>
                  - Drag activities from the palette onto the canvas.<br/>
                  - Click an edge to label it (required for Decision branches).<br/>
                  - Fork splits into parallel paths; Join waits for all.
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="overline">Activity</div>
                {(selected.data.type !== "start" && selected.data.type !== "end" && !readOnly) && (
                  <button onClick={deleteSelected} data-testid="designer-delete-node-btn" className="text-[var(--danger)] hover:bg-[var(--surface-2)] p-1 rounded transition">
                    <Trash size={14} />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-[var(--text-2)]">Label</Label>
                  <Input value={selected.data.label} disabled={readOnly} onChange={(e) => updateSelected({ label: e.target.value })} data-testid="designer-node-label-input" className="mt-1 glass-subtle border-[var(--border)]" />
                </div>
                <div>
                  <Label className="text-xs text-[var(--text-2)]">Type</Label>
                  <div className="mt-1 px-3 py-2 rounded-md bg-[var(--surface)] border border-[var(--border)] text-sm capitalize font-mono">{selected.data.type.replace("_", " ")}</div>
                </div>
                {(selected.data.type !== "start" && selected.data.type !== "end" && selected.data.type !== "fork" && selected.data.type !== "join") && (
                  <>
                    <div>
                      <Label className="text-xs text-[var(--text-2)]">Executor type</Label>
                      <Select value={selected.data.executor_type || "auto"} onValueChange={(v) => updateSelected({ executor_type: v })} disabled={readOnly}>
                        <SelectTrigger data-testid="designer-node-executor-type" className="mt-1 glass-subtle border-[var(--border)]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto (system)</SelectItem>
                          <SelectItem value="user">Specific user</SelectItem>
                          <SelectItem value="role">Role</SelectItem>
                          <SelectItem value="external">External (signatory / vendor)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {(selected.data.executor_type === "role" || selected.data.executor_type === "user" || selected.data.executor_type === "external") && (
                      <div>
                        <Label className="text-xs text-[var(--text-2)]">{selected.data.executor_type === "role" ? "Role" : selected.data.executor_type === "user" ? "User email" : "External ref"}</Label>
                        <Input value={selected.data.executor_ref || ""} disabled={readOnly} onChange={(e) => updateSelected({ executor_ref: e.target.value })} placeholder={selected.data.executor_type === "role" ? "manager" : "email@company.com"} data-testid="designer-node-executor-ref" className="mt-1 glass-subtle border-[var(--border)] font-mono text-xs" />
                      </div>
                    )}
                  </>
                )}
                {selected.data.type === "decision" && (
                  <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300">
                    <div className="font-semibold mb-1">Decision node</div>
                    Label each outgoing edge (click the edge, set a branch label). Instance.advance() will pick the edge whose label matches the passed branch.
                  </div>
                )}
                <div>
                  <Label className="text-xs text-[var(--text-2)]">Notes / Config (JSON)</Label>
                  <Textarea rows={3} disabled={readOnly} value={selected.data.config ? JSON.stringify(selected.data.config, null, 2) : "{}"} onChange={(e) => { try { updateSelected({ config: JSON.parse(e.target.value) }); } catch {} }} data-testid="designer-node-config-input" className="mt-1 glass-subtle border-[var(--border)] font-mono text-xs" />
                </div>
              </div>
            </>
          )}
        </GlassCard>
      </div>
    </PageShell>
  );
}

export default function WorkflowDesigner() {
  return (
    <ReactFlowProvider>
      <Designer />
    </ReactFlowProvider>
  );
}
